import { useState, useEffect, useCallback } from 'react';

interface AccessibilityStatus {
  isAccessible: boolean;
  isChecking: boolean;
  error: string | null;
  retryCount: number;
}

export function useYouTubeAccessibility() {
  const [status, setStatus] = useState<AccessibilityStatus>({
    isAccessible: true,
    isChecking: true,
    error: null,
    retryCount: 0
  });

  // Development helper: Force accessibility state
  const forceAccessibilityState = useCallback((isAccessible: boolean, error?: string) => {
    setStatus({
      isAccessible,
      isChecking: false,
      error: error || (isAccessible ? null : 'YouTube access forced to blocked'),
      retryCount: 0
    });
  }, []);

  // Expose force function globally in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      (window as any).forceYouTubeAccessibility = forceAccessibilityState;
    }
    return () => {
      if (process.env.NODE_ENV === 'development') {
        delete (window as any).forceYouTubeAccessibility;
      }
    };
  }, [forceAccessibilityState]);

  // Check if we should use China network simulation via globalping
  const shouldSimulateChinaAccess = useCallback(() => {
    return process.env.NODE_ENV === 'development' && 
           localStorage.getItem('simulate_china_network') === 'true';
  }, []);

  // Use globalping to test YouTube accessibility from China
  const checkAccessibilityFromChina = useCallback(async () => {
    try {
      const response = await fetch('/api/youtube/accessibility-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          simulateFromChina: true,
          target: 'youtube.com'
        })
      });
      
      const data = await response.json();
      return {
        isAccessible: data.isAccessible,
        error: data.error || (data.isAccessible ? null : 'YouTube is not accessible from China')
      };
    } catch (error) {
      console.error('Globalping accessibility check failed:', error);
      return {
        isAccessible: false,
        error: 'Unable to check China network accessibility'
      };
    }
  }, []);

  const checkAccessibility = useCallback(async () => {
    setStatus(prev => ({ ...prev, isChecking: true, error: null }));
    
    // Use globalping simulation if enabled
    if (shouldSimulateChinaAccess()) {
      console.log('🧪 Using globalping to simulate China YouTube access');
      try {
        // const chinaResult = await checkAccessibilityFromChina();
        // setStatus({
        //   isAccessible: chinaResult.isAccessible,
        //   isChecking: false,
        //   error: chinaResult.error,
        //   retryCount: 0
        // });
        // return chinaResult.isAccessible;
        return true;
      } catch (error) {
        console.error('China simulation failed:', error);
        setStatus({
          isAccessible: false,
          isChecking: false,
          error: 'China network simulation failed',
          retryCount: 0
        });
        return false;
      }
    }
    
    try {
      // Method 1: Try to load YouTube favicon with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch('https://www.youtube.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-cache'
      });
      
      clearTimeout(timeoutId);
      
      // If we get here without error, YouTube might be accessible
      // But no-cors doesn't give us response status, so we need another check
      
      // Method 2: Try YouTube iframe API
      const testIframe = document.createElement('iframe');
      testIframe.style.display = 'none';
      testIframe.src = 'https://www.youtube.com/iframe_api';
      
      const iframePromise = new Promise<boolean>((resolve) => {
        let resolved = false;
        
        testIframe.onload = () => {
          if (!resolved) {
            resolved = true;
            resolve(true);
          }
        };
        
        testIframe.onerror = () => {
          if (!resolved) {
            resolved = true;
            resolve(false);
          }
        };
        
        // Timeout after 5 seconds
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            resolve(false);
          }
        }, 5000);
      });
      
      document.body.appendChild(testIframe);
      const isAccessible = await iframePromise;
      document.body.removeChild(testIframe);
      
      setStatus({
        isAccessible,
        isChecking: false,
        error: isAccessible ? null : 'YouTube is not accessible in your region',
        retryCount: 0
      });
      
      return isAccessible;
    } catch (error) {
      console.error('Error checking YouTube accessibility:', error);
      
      setStatus(prev => ({
        isAccessible: false,
        isChecking: false,
        error: 'Unable to access YouTube',
        retryCount: prev.retryCount + 1
      }));
      
      return false;
    }
  }, []);

  const retry = useCallback(() => {
    if (status.retryCount < 3) {
      checkAccessibility();
    }
  }, [status.retryCount, checkAccessibility]);

  useEffect(() => {
    // Check on mount
    checkAccessibility();
    
    // Recheck when network changes
    const handleOnline = () => {
      if (!status.isAccessible) {
        checkAccessibility();
      }
    };
    
    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return {
    ...status,
    checkAccessibility,
    retry,
    forceAccessibilityState
  };
}